import { Alert, Autocomplete, Box, CircularProgress } from '@mui/material';
import useCompany from '../../hooks/useCompany';
import { PROFESSIONAL_ORGANIZATION_OPTIONS, surveyOptions } from '../../utils/constant';
import StyledBox from './styled/SignUp/StyledBox';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useEffect } from 'react';
import { Typography } from '../new/Typography';
import { TextField } from '../new/inputs/TextField';
import Select from '../new/inputs/Select';
import { Button } from '../new/Button';

const CompanyInformation = () => {
  const {
    fieldsMapper,
    inputWrapperStyles,
    companyInformation,
    errors,
    onRegisterOrganization,
    onSurveyAnswerDetailChange,
    onChange,
    onSelect,
    setErrors,
  } = useCompany();

  const isCreatingOrganization = useSelector((state: RootState) => state.user.isCreatingOrganization);

  const networkError = useSelector((state: RootState) => state.user.createOrganizationNetworkError);
  const networkErrorStack = useSelector((state: RootState) => state.user.createOrganizationNetworkErrorStack);

  useEffect(() => {
    if (networkError === 'Promocode not found') {
      setErrors((prev) => ({ ...prev, promoCode: 'Invalid promo code' }));
    }
    if (networkError === 'Promocode is expired') {
      setErrors((prev) => ({ ...prev, promoCode: 'Promo code is expired' }));
    }
    if (networkError === 'Validation error at body') {
      const clear = ['domainNames', 'companyPhoneNumber'];
      let bodyErrors =
        networkErrorStack
          ?.map((err) => clear.reduce((acc, word) => acc.replace(new RegExp(word, 'gi'), ''), err).trim())
          .filter(Boolean)
          .join('\n') ?? '';
      setErrors((prev) => ({ ...prev, bodyErrors }));
    }
  }, [networkError, networkErrorStack, setErrors]);

  const showNewtorkErrorAlert =
    networkError && networkError !== 'Promocode not found' && networkError !== 'Promocode is expired';

  const renderFieldBasedOnSurveyOption = () => {
    switch (companyInformation.surveyAnswer) {
      case 'Referral from colleague or peer':
        return (
          <TextField
            key="surveyAnswerDetail-whoReffered"
            label="Who referred you? Help us thank them!"
            value={companyInformation['surveyAnswerDetail']}
            onChange={onSurveyAnswerDetailChange}
            name="surveyAnswerDetail"
            id="surveyAnswerDetail"
          />
        );
      case 'Conference, CLE, or trade show':
        return (
          <TextField
            key="surveyAnswerDetail-whichEvent"
            label="Which event?"
            value={companyInformation['surveyAnswerDetail']}
            onChange={onSurveyAnswerDetailChange}
            name="surveyAnswerDetail"
            id="surveyAnswerDetail"
          />
        );
      case 'Professional organization':
        return (
          <Select
            data={PROFESSIONAL_ORGANIZATION_OPTIONS}
            onChange={onSurveyAnswerDetailChange}
            value={companyInformation['surveyAnswerDetail']}
            containerSx={{ marginBottom: '1.5rem' }}
            label="Which one?"
            key="surveyAnswerDetail-whichOrganization"
            disabled={false}
            error=""
          />
        );
      case 'Other':
        return (
          <TextField
            key="surveyAnswerDetail-other"
            placeholder="Please specify (optional)"
            value={companyInformation['surveyAnswerDetail']}
            onChange={onSurveyAnswerDetailChange}
            name="surveyAnswerDetail"
            id="surveyAnswerDetail"
          />
        );
    }
  };

  const renderFields = () => {
    const rows: JSX.Element[] = [];
    let currentRow: {
      label: string;
      isRow: boolean;
      value: string;
      type: string;
      name: string;
      error: string;
      autoComplete?: string;
      id: string;
      maxLength?: number;
      sx?: any;
      options?: any[];
      inputRef?: any;
    }[] = [];

    fieldsMapper.forEach((field, index) => {
      if (field.isRow) {
        currentRow.push(field);
      }
      if (!field.isRow || index === fieldsMapper.length - 1 || currentRow.length === 3) {
        if (currentRow.length > 0) {
          rows.push(
            <div
              key={`row-${rows.length}`}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.625rem',
              }}
            >
              {currentRow.map((rowField, idx) =>
                rowField.type === 'textfield' ? (
                  <TextField
                    key={rowField.id}
                    placeholder={field.placeholder ? field.placeholder : 'Add...'}
                    inputProps={{ maxLength: rowField.maxLength }}
                    onChange={onChange}
                    error={rowField.error}
                    label={rowField.label}
                    containerSX={rowField.sx ?? {}}
                    value={companyInformation[rowField.name as keyof typeof companyInformation] as string}
                    name={rowField.name}
                    id={rowField.name}
                    inputRef={rowField.inputRef}
                    autoComplete={rowField.autoComplete}
                  />
                ) : (
                  <Select
                    data={rowField.options}
                    onChange={(e) => onSelect(e, rowField.name)}
                    value={rowField.value}
                    label={rowField.label}
                    containerSx={rowField.sx ?? {}}
                    key={rowField.id}
                    autoComplete={rowField.autoComplete}
                    disabled={false}
                    error={rowField.error !== '' ? rowField.error : undefined}
                  />
                ),
              )}
            </div>,
          );
          currentRow = [];
        }

        if (!field.isRow) {
          rows.push(
            field.type === 'textfield' ? (
              <TextField
                key={field.id}
                placeholder={field.placeholder ? field.placeholder : 'Add...'}
                inputProps={{ maxLength: field.maxLength }}
                value={companyInformation[field.name as keyof typeof companyInformation] as string}
                onChange={onChange}
                error={field.error}
                label={field.label}
                name={field.name}
                id={field.name}
                autoComplete={field.autoComplete}
              />
            ) : field.name === 'organizationType' ? (
              <div key={field.id}>
                <Select
                  data={field.options}
                  onChange={(e) => onSelect(e, field.name)}
                  value={field.value}
                  key={`${field.id}-select`}
                  label={field.label}
                  containerSx={{ marginBottom: field.error ? '' : '1.5rem' }}
                  disabled={false}
                  autoComplete={field.autoComplete}
                  error={field.error !== '' ? field.error : undefined}
                />
                {field.value === 'Other' && (
                  <TextField
                    key={`${field.id}-textfield`}
                    placeholder="Please specify (optional)"
                    value={companyInformation['customOrgType'] as string}
                    onChange={onChange}
                    name="customOrgType"
                    id="customOrgType"
                  />
                )}
              </div>
            ) : (
              <Select
                data={field.options as any}
                onChange={(e) => onSelect(e, field.name)}
                value={field.value}
                key={field.id}
                label={field.label}
                containerSx={{ marginBottom: field.error ? '' : '1.5rem' }}
                disabled={false}
                autoComplete={field.autoComplete}
                error={field.error !== '' ? field.error : undefined}
              />
            ),
          );
        }
      }
    });
    return rows;
  };

  if (isCreatingOrganization) {
    return (
      <div
        style={{
          backgroundColor: '#0000005e',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#FFF' }} size={50} />
        <Typography sx={{ marginTop: '0.9375rem', fontSize: '1.25rem', color: '#FFF' }}>
          Creating Company... Please wait!
        </Typography>
      </div>
    );
  }

  return (
    <StyledBox>
      <Typography variant="h5" sx={{ fontSize: '1.875rem', mt: 3 }}>
        Create Organization
      </Typography>
      <Box component="form" noValidate sx={{ mt: 1.5, width: '60%', height: '100%', justifySelf: 'center' }}>
        <div style={inputWrapperStyles}>
          {renderFields()}
          <Select
            data={surveyOptions}
            onChange={(e) => onSelect(e, 'surveyAnswer')}
            placeholder="Select..."
            label="How did you hear about us?"
            value={companyInformation.surveyAnswer}
            disabled={false}
            error={errors.surveyAnswer}
            containerSx={{ marginBottom: errors.surveyAnswer ? '' : '1.5rem' }}
          />
          {renderFieldBasedOnSurveyOption()}
          <Button
            type="primary"
            sx={{
              marginBottom: showNewtorkErrorAlert ? '1.25rem' : '4.375rem',
            }}
            onClick={onRegisterOrganization}
          >
            Get Started
          </Button>
          {showNewtorkErrorAlert && (
            <Alert sx={{ marginBottom: '3.125rem', whiteSpace: 'pre-line', alignItems: 'center' }} severity="error">
              {errors.bodyErrors ?? networkError}
            </Alert>
          )}
        </div>
      </Box>
    </StyledBox>
  );
};

export default CompanyInformation;
